/**
 * <g-task-list> - reorderable task list, with subtasks as a hierarchy of tasks.
 *
 * Two render modes:
 *   mode="full" (default) — interactive list: drag-reorder (SortableJS),
 *     checkboxes, pills, due dates, subtasks nested under their parent.
 *   mode="compact" — read-only "n/m done" summary + thin progress bar over the
 *     tasks it is given; no drag, no events. Meant to embed inside a <g-card>,
 *     fed a card's subtasks.
 *
 * Data goes in via JS properties (the golden rule): set `.tasks` (and the
 * optional `.config`) — never through attributes. Re-assign `.tasks` after
 * every load/filter; the component re-renders.
 *
 * SortableJS must be loaded globally (it is, in Gainable apps).
 *
 * SUBTASKS ARE TASKS. There is one kind of record. A subtask is a task whose
 * parent field (`config.fields.parent`, e.g. `parentTask`) names another task;
 * the list is a hierarchy VIEW over those records, nothing more. Every row, at
 * every depth, is the same <g-task-item> showing that task's own data — its own
 * checkbox, pills, due date and tint — and every row's events are about that
 * task. Nothing here stores, edits or invents subtask data: a subtask is
 * created, opened and changed exactly the way any task is, through the host's
 * create and edit forms.
 *
 * - A task whose parent is in the list is drawn under that parent, collapsed
 *   until its parent's count badge is clicked. Deeper levels nest the same way.
 * - A task whose parent is NOT in the list (filtered out, on no page, missing)
 *   is drawn as a top-level row rather than lost, and so is a task in a loop.
 * - Clicking any row emits `task-clicked` for that row's task: the host opens
 *   its edit form.
 * - With `add-subtasks`, hovering a row reveals "add subtask", which emits
 *   `add-subtask` with that task as the parent: the host opens its create form
 *   with the parent preselected.
 * - Drag reorders a task among its siblings. A dragged task carries its
 *   subtasks with it; moving a task to another parent is an edit, made in the
 *   form.
 *
 * @attr {string}  mode          - `full` (default) | `compact`
 * @attr {string}  empty-text    - shown when there are no tasks (default 'No tasks')
 * @attr {string}  empty-icon    - Font Awesome classes for the empty state
 * @attr {boolean} loading       - presence = skeleton rows
 * @attr {number}  page-size     - paginate the top-level rows (opt-in)
 * @attr {boolean} no-header     - hide the column headings
 * @attr {string}  title-heading - first column's heading (default 'Task')
 * @attr {boolean} add-subtasks  - presence = rows offer "add subtask" (needs config.fields.parent)
 *
 * @fires task-moved   - { taskId, parentId, oldIndex, newIndex, tasks, afterId, beforeId }
 * @fires task-toggled - { taskId, done, task, field?, value? }
 * @fires task-clicked - { taskId, task }
 * @fires add-subtask  - { parentId, parentField, parent }
 * @fires page-change  - { page, totalPages, pageSize }
 */
import { resolveStatus, statusAttr, ensureStatusStyles, normalizeStatus } from '../util/status.js';
import { PILL_COL, DUE_COL, LEAD_COLS } from './columns.js';
import { ensureDragStyles, DRAG_CLASSES } from '../util/drag.js';
import { neighbours } from '../util/order.js';

/** `taskStatus` -> `Task status`. Only used when a spec names no `label`. */
function humanizeField(field) {
  return String(field || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

// Monotonic across every instance — two lists may hold the same object.
let uidSeq = 0;

export class GTaskList extends HTMLElement {
  constructor() {
    super();
    this._tasks = [];
    this._config = null;
    this._renderScheduled = false;
    this._isDragging = false;
    this._pendingRender = false;
    // One Sortable per container: the top-level list, and each task's children.
    this._sortables = new Map();
    this._taskStatus = null;
    // Ids of tasks whose subtasks are open. Subtasks start collapsed; this
    // outlives each render so expanding a row isn't undone by the next refresh.
    this._expanded = new Set();
    // Current page. Only meaningful when `page-size` is set — see pageSize().
    this._page = 1;
    // Fallback ids for tasks that carry none of their own — see _taskId.
    this._uids = new WeakMap();
  }

  static get observedAttributes() { return ['mode', 'empty-text', 'loading', 'empty-icon', 'page-size', 'no-header', 'title-heading', 'add-subtasks']; }

  get emptyIcon() {
    const v = this.getAttribute('empty-icon');
    return v && /^[\w\s-]+$/.test(v) ? v : 'fa-regular fa-list-check';
  }
  attributeChangedCallback() { this.scheduleRender(); }

  connectedCallback() {
    ensureDragStyles();
    this.classList.add('block');
    ensureStatusStyles();
    if (!this._root) { this._root = document.createElement('div'); this.appendChild(this._root); }
    this.scheduleRender();
    this.addEventListener('change', this._handleChange);
    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._handleChange);
    this.removeEventListener('click', this._handleClick);
    this._destroySortables();
  }

  // ── Public API ──

  get tasks() { return this._tasks; }
  set tasks(v) {
    this._tasks = Array.isArray(v) ? v : [];
    // Clamp: deleting the last task on the final page would otherwise leave the
    // view parked on a page that no longer exists, showing nothing.
    const totalPages = this._totalPages();
    if (this._page > totalPages) this._page = totalPages;
    this.scheduleRender();
  }

  // .data is an alias of .tasks so uniform host code (like <g-table>/<g-card-grid>) works.
  get data() { return this._tasks; }
  set data(v) { this.tasks = v; }

  get config() { return this._config; }
  set config(c) { this._config = c && typeof c === 'object' ? c : null; this.scheduleRender(); }

  // Row tint. Either a callback — .taskStatus = (task, i) => 'error' | null —
  // or the declarative twin at config.status ({ field, colorMap, colorRule }).
  // The callback wins when both are present. Values are constrained to the
  // semantic tokens in util/status.js; anything else resolves to no tint.
  get taskStatus() { return this._taskStatus; }
  set taskStatus(fn) { this._taskStatus = typeof fn === 'function' ? fn : null; this.scheduleRender(); }

  _statusSpec() { return this._taskStatus || (this._config && this._config.status) || null; }

  get mode() { return this.getAttribute('mode') === 'compact' ? 'compact' : 'full'; }
  get emptyText() { return this.getAttribute('empty-text') || 'No tasks'; }
  get loading() { return this.hasAttribute('loading'); }

  /**
   * Do rows offer "add subtask"?
   *
   * Needs a parent field as well as the attribute: the event tells the host
   * which field to preset, and without one there is nothing a subtask could be
   * linked by. `add-subtasks="false"` counts as off — a server-side template
   * interpolating a boolean writes the STRING "false".
   */
  get addSubtasks() {
    return !!this._parentKey()
      && this.hasAttribute('add-subtasks') && this.getAttribute('add-subtasks') !== 'false';
  }
  set addSubtasks(v) {
    if (v) this.setAttribute('add-subtasks', ''); else this.removeAttribute('add-subtasks');
  }

  // ── Field mapping (config.fields remaps; sane defaults otherwise) ──

  _fmap() { return (this._config && this._config.fields) || {}; }

  /**
   * The key for a row: reuse across renders, event routing (`_taskById`) and
   * drag reconciliation all hang off it. An object with no id of its own gets
   * a WeakMap-held one, stable for as long as the object lives, so two such
   * rows never collapse onto one another.
   */
  _taskId(t) {
    const k = this._fmap().id || 'id';
    let id = t && t[k];
    if (id == null && t) id = t._id;
    if (id == null && t && typeof t === 'object') return this._uidFor(t);
    return id == null ? '' : String(id);
  }
  _uidFor(t) {
    let id = this._uids.get(t);
    if (!id) { id = `g-task-u${++uidSeq}`; this._uids.set(t, id); }
    return id;
  }
  _title(t) { return t[this._fmap().title || 'title']; }

  /**
   * Stage-driven completion: `config.done = { field, doneValue, undoValue }`.
   *
   * Most task data does not carry a boolean. A list imported from a spreadsheet
   * says "Done" / "In Progress" / "Not Started" in a status column, and a
   * boolean read of that is inverted-wrong: `!!"Not Started"` is `true`.
   * Returns null when no stage spec is configured, so `_done()` keeps its
   * boolean path for hosts that do have a real flag.
   */
  _doneSpec() {
    const s = this._config && this._config.done;
    return (s && typeof s === 'object' && s.field) ? s : null;
  }

  _done(t) {
    const spec = this._doneSpec();
    if (spec) {
      const raw = t && t[spec.field];
      // The stage is often a populated ref ({_id, name, color}), not a string.
      const value = (raw && typeof raw === 'object')
        ? raw[spec.displayField || 'name']
        : raw;
      return value != null && String(value) === String(spec.doneValue);
    }
    return !!t[this._fmap().done || 'done'];
  }
  _dueKey() { return this._fmap().dueDate || 'dueDate'; }
  _due(t) { return t[this._dueKey()]; }

  /**
   * Does this list HAVE a due column? One answer, read by both the rows and the
   * heading. Asked of the DATA, across the whole list rather than the page, so
   * the column does not appear and disappear as you page through: the generator
   * always fills `fields.dueDate` in, so the declaration alone cannot decide it.
   */
  _hasDueColumn() {
    const k = this._dueKey();
    return this._tasks.some((t) => t && t[k] != null && t[k] !== '');
  }

  // ── Hierarchy: `config.fields.parent` ──

  _parentKey() { return this._fmap().parent || null; }

  /** The parent's id, whether the link arrives populated, as a one-element array, or bare. */
  _parentIdOf(t) {
    const key = this._parentKey();
    if (!key || !t) return null;
    let v = t[key];
    if (Array.isArray(v)) v = v[0];
    if (v == null || v === '') return null;
    const id = typeof v === 'object' ? (v._id ?? v.id) : v;
    return id == null || id === '' ? null : String(id);
  }

  /**
   * { roots, kids }: the top-level tasks, and each task's direct children, both
   * in list order. Without a parent field every task is a root.
   */
  _tree() {
    if (!this._parentKey()) return { roots: this._tasks, kids: new Map() };
    const byId = new Map(this._tasks.map((t) => [this._taskId(t), t]));
    const kids = new Map();
    const roots = [];
    for (const t of this._tasks) {
      const id = this._taskId(t);
      const pid = this._parentIdOf(t);
      let nested = !!pid && pid !== id && byId.has(pid);
      // Walk up: a loop anywhere above keeps this task a row of its own.
      for (let cur = pid, seen = new Set([id]); nested && cur; ) {
        if (seen.has(cur)) { nested = false; break; }
        seen.add(cur);
        const up = this._parentIdOf(byId.get(cur));
        cur = up && byId.has(up) ? up : null;
      }
      if (nested) {
        if (!kids.has(pid)) kids.set(pid, []);
        kids.get(pid).push(t);
      } else {
        roots.push(t);
      }
    }
    return { roots, kids };
  }

  /** The top-level rows — what pagination counts and the top-level drag orders. */
  _rows() { return this._tree().roots; }

  /**
   * Secondary metadata rendered as chips: `config.pills = [{ field, colorMap?,
   * displayField?, label? }]`. One GROUP PER SPEC, in declared order — every
   * spec yields an entry even when this task has no value for it, because the
   * groups are columns. Colour mirrors <g-table>'s badge renderer: look the
   * VALUE up in `colorMap`. An array value (multi-owner) becomes one chip each.
   */
  _pillGroups(t) {
    const specs = (this._config && Array.isArray(this._config.pills)) ? this._config.pills : [];
    return specs.filter((spec) => spec && spec.field).map((spec) => {
      const raw = t ? t[spec.field] : null;
      const key = spec.displayField || 'name';
      const toLabel = (v) => (v && typeof v === 'object') ? v[key] : v;
      const values = (raw == null || raw === '') ? [] : (Array.isArray(raw) ? raw : [raw])
        .map(toLabel)
        .filter((v) => v != null && v !== '');
      const map = (spec.colorMap && typeof spec.colorMap === 'object') ? spec.colorMap : {};
      return {
        field: spec.field,
        label: spec.label || humanizeField(spec.field),
        chips: values.map((v) => ({ label: String(v), color: normalizeStatus(map[v]) })),
      };
    });
  }

  // ── Render ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      // Never rebuild the DOM out from under an active drag — queue it.
      if (this._isDragging) { this._pendingRender = true; return; }
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    if (this.mode === 'compact') { this._teardownSurface(); this._renderCompact(); return; }
    this._renderFull();
  }

  /** How many of the given tasks are done — each task counts once, at any depth. */
  _renderCompact() {
    const total = this._tasks.length;
    const done = this._tasks.filter((t) => this._done(t)).length;
    const pc = (this._config && this._config.progressColor) || {};
    const color = total === 0 ? (pc.empty || 'neutral')
      : done >= total ? (pc.full || 'success')
      : done > 0 ? (pc.partial || 'primary')
      : (pc.empty || 'neutral');
    const showCount = !(this._config && this._config.showCompletedCount === false);
    this._root.innerHTML = `
      <div class="flex items-center gap-2">
        <progress class="progress progress-${color} h-1.5 flex-1" value="${done}" max="${total || 1}"></progress>
        ${showCount ? `<span class="text-xs text-base-content/60 whitespace-nowrap">${done}/${total} done</span>` : ''}
      </div>`;
  }

  _renderFull() {
    if (this.loading) {
      this._teardownSurface();
      // Reserve the pill cell when pills are configured — the loading state is
      // supposed to be the same shape as the real row.
      const hasPills = !!(this._config && Array.isArray(this._config.pills) && this._config.pills.length);
      this._root.innerHTML = `<div class="rounded-lg border border-base-300 bg-base-100 overflow-hidden">${repeat(4, () => skeletonRow(hasPills))}</div>`;
      return;
    }
    if (!this._tasks.length) {
      this._teardownSurface();
      this._root.innerHTML = `<div class="text-center py-12 text-base-content/50">
        <i class="${escapeHtml(this.emptyIcon)} text-3xl mb-2 block"></i>${escapeHtml(this.emptyText)}</div>`;
      return;
    }

    // SortableJS OWNS the row order. We reconcile around it: the surface is
    // built once and kept, rows are matched to tasks by id and reused, and moved
    // only when they are genuinely out of position. After a drop the DOM is
    // already in the new order, so re-pushing `.tasks` moves nothing at all.
    const surface = this._ensureSurface();
    const tree = this._tree();
    this._taskById = new Map(this._tasks.map((t) => [this._taskId(t), t]));
    // Absolute index for rowStatus: a function of the record's place in the
    // whole list, not of where it happens to sit on a page.
    const indexOf = new Map(this._tasks.map((t, i) => [t, i]));
    const spec = this._statusSpec();
    // Decided ONCE for the whole list, then handed to both the rows and the
    // heading — a per-row decision is what makes columns drift.
    const showDue = this._hasDueColumn();
    const canAddSubtask = this.addSubtasks;

    const prev = this._itemById || new Map();
    const next = new Map();

    const place = (container, tasks, depth) => {
      let cursor = container.firstElementChild;
      for (const t of tasks) {
        const id = this._taskId(t);
        let item = prev.get(id);
        if (!item) {
          item = document.createElement('g-task-item');
          item.setAttribute('task-id', id);
        }
        next.set(id, item);
        // Only touch the DOM when the row is not already where it belongs —
        // re-inserting a custom element disconnects and reconnects it. A task
        // that changed parent is moved here, subtree and all.
        if (cursor === item) cursor = cursor.nextElementSibling;
        else container.insertBefore(item, cursor);

        const kids = tree.kids.get(id) || [];
        const status = resolveStatus(t, indexOf.get(t), spec);
        item.task = {
          id,
          depth,
          title: this._title(t),
          done: this._done(t),
          due: this._formatDue(this._due(t)),
          showDue,
          pillGroups: this._pillGroups(t),
          status,
          statusAttr: statusAttr(status),
          childCount: kids.length,
          // Subtasks stay collapsed until asked for; the list owns the set so
          // the open/closed state survives a re-render (a data refresh, a drop).
          expanded: this._expanded.has(id),
          canAddSubtask,
        };
        place(item.childList, kids, depth + 1);
      }
      // Whatever is still past the cursor is not this container's any more:
      // gone from the list, off this page, or moved under another parent (in
      // which case a later placement re-inserts the same element there).
      while (cursor) {
        const after = cursor.nextElementSibling;
        cursor.remove();
        cursor = after;
      }
    };
    place(this._listEl, this._pageTasks(), 0);
    this._itemById = next;

    this._syncChrome(surface, this._renderHeader(showDue), this._renderPagination());
    this._syncSortables();
  }

  /**
   * The parts around the rows: one bordered surface holding the heading strip,
   * the row container, and the pager. Built once and reused, because the row
   * container is what the top-level Sortable is bound to.
   *
   * The heading and pager are SIBLINGS of the row container, never inside it:
   * a non-draggable node among the rows throws Sortable's indexes off.
   */
  _ensureSurface() {
    if (this._surfaceEl && this._surfaceEl.isConnected && this._listEl) return this._surfaceEl;
    const surface = document.createElement('div');
    surface.className = 'rounded-lg border border-base-300 bg-base-100 overflow-hidden';
    const list = document.createElement('div');
    list.setAttribute('data-g-task-root', '');
    surface.appendChild(list);
    this._root.innerHTML = '';
    this._root.appendChild(surface);
    this._surfaceEl = surface;
    this._listEl = list;
    this._itemById = new Map();
    this._destroySortables();          // the old instances pointed at dead containers
    return surface;
  }

  /** Swap the heading/pager markup only when it actually changed. */
  _syncChrome(surface, headHtml, footHtml) {
    const sync = (cur, html, put) => {
      if (!html) { if (cur) cur.remove(); return null; }
      if (cur && cur.outerHTML === html.trim()) return cur;
      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      const el = wrap.firstElementChild;
      if (cur) cur.replaceWith(el); else put(el);
      return el;
    };
    this._headEl = sync(this._headEl, headHtml, (el) => surface.insertBefore(el, this._listEl));
    this._footEl = sync(this._footEl, footHtml, (el) => surface.appendChild(el));
  }

  _formatDue(v) {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const days = Math.round((due - today) / 86400000);
    if (days < 0) return { label: `${Math.abs(days)}d overdue`, tone: 'overdue' };
    if (days === 0) return { label: 'Today', tone: 'soon' };
    if (days === 1) return { label: 'Tomorrow', tone: 'soon' };
    if (days <= 2) return { label: `${days}d`, tone: 'soon' };
    return { label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), tone: 'normal' };
  }

  /**
   * Column headers — the row that says which chip is which. Only when there is
   * more than a title to label. The leading spacers mirror the row's own
   * leading cells exactly (grip, checkbox); get one wrong and every heading
   * sits half a column off.
   */
  _renderHeader(hasDue = this._hasDueColumn()) {
    if (this.mode !== 'full') return '';
    if (this.hasAttribute('no-header')) return '';
    const groups = this._pillGroups(null);
    if (!groups.length && !hasDue) return '';

    const pillHeads = groups.map((g) =>
      `<span class="hidden sm:block ${PILL_COL} shrink-0 truncate">${escapeHtml(g.label)}</span>`).join('');
    const dueHead = hasDue
      ? `<span class="${DUE_COL} shrink-0 text-right">Due</span>`
      : '';

    return `
      <div class="g-task-head flex items-center gap-2 px-2 py-2 border-b border-base-300
                  bg-base-200/40 text-xs font-medium uppercase tracking-wide text-base-content/60">
        ${LEAD_COLS.map((w) => `<span class="${w} shrink-0" aria-hidden="true"></span>`).join('')}
        <span class="flex-1 min-w-0 truncate">${escapeHtml(this.titleHeading)}</span>
        ${pillHeads}
        ${dueHead}
      </div>`;
  }

  /** What the first column is called. `title-heading` overrides it. */
  get titleHeading() { return this.getAttribute('title-heading') || 'Task'; }

  // ── Pagination ──
  //
  // Mirrors <g-table>: a `page-size` attribute, a `page-change` event, the same
  // footer — but OPT-IN. Pages count top-level rows; a task's subtasks are on
  // the same page as the task.
  get pageSize() {
    const raw = this.getAttribute('page-size');
    if (raw === null) return null;
    const v = parseInt(raw, 10);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  get paginated() { return this.pageSize !== null && this._rows().length > this.pageSize; }

  _totalPages() {
    const size = this.pageSize;
    if (!size) return 1;
    return Math.max(1, Math.ceil(this._rows().length / size));
  }

  _pageStart() {
    const size = this.pageSize;
    if (!size) return 0;
    return (Math.min(this._page, this._totalPages()) - 1) * size;
  }

  /** The top-level rows currently on screen. */
  _pageTasks() {
    const size = this.pageSize;
    const rows = this._rows();
    if (!size) return rows;
    const start = this._pageStart();
    return rows.slice(start, start + size);
  }

  setPage(page) {
    const totalPages = this._totalPages();
    const next = Math.max(1, Math.min(page, totalPages));
    if (next === this._page) return;
    this._page = next;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('page-change', {
      detail: { page: this._page, totalPages, pageSize: this.pageSize }, bubbles: true, composed: true,
    }));
  }

  _renderPagination() {
    if (!this.paginated) return '';
    const total = this._rows().length;
    const start = this._pageStart();
    const end = Math.min(start + this.pageSize, total);
    const totalPages = this._totalPages();
    return `
      <div class="border-t border-base-300 px-5 py-3 flex justify-between items-center">
        <span class="text-sm text-base-content/60 truncate">Showing ${start + 1}–${end} of ${total}</span>
        <div class="join">
          <button class="join-item btn btn-sm" data-page="prev"${this._page <= 1 ? ' disabled' : ''}>«</button>
          <button class="join-item btn btn-sm">${this._page}</button>
          <button class="join-item btn btn-sm" data-page="next"${this._page >= totalPages ? ' disabled' : ''}>»</button>
        </div>
      </div>`;
  }

  // ── SortableJS (full mode only) ──
  //
  // ONE instance per container, for the life of that container: the top-level
  // list, and the children container of every task with two or more subtasks
  // (one child has nothing to be reordered against). Instances are not shared
  // through a `group`, so a drag stays among its siblings.

  _syncSortables() {
    if (!this._listEl) return;
    if (typeof Sortable === 'undefined') {
      console.error('[g-task-list] SortableJS not found. Include it before using g-task-list.');
      return;
    }
    const want = new Set([this._listEl]);
    for (const item of this._itemById.values()) {
      if ((item.task && item.task.childCount) > 1) want.add(item.childList);
    }
    for (const [container, sortable] of this._sortables) {
      if (!want.has(container) || !container.isConnected) {
        try { sortable.destroy(); } catch { /* already gone */ }
        this._sortables.delete(container);
      }
    }
    for (const container of want) {
      if (!this._sortables.has(container)) this._sortables.set(container, this._createSortable(container));
    }
  }

  _createSortable(container) {
    return new Sortable(container, {
      // Pointer events instead of native HTML5 DnD: native DnD's start is the
      // browser's decision, it is the path that failed (the row took the grab
      // and never moved), and it cannot be driven by an automated test. The
      // fallback carries the drag with a clone of the row, which is why
      // <g-task-item> must survive being cloned — see its connectedCallback.
      forceFallback: true,
      // The surface is `overflow-hidden`; without this the carried row is
      // clipped by its own list the moment it leaves the container.
      fallbackOnBody: true,
      // "3 to 5 are probably good values" for lists whose rows are also
      // clickable. Ours are: a row opens the editor.
      fallbackTolerance: 3,
      // Two jobs. A press on a control must not become a drag (preventOnFilter
      // stays false or the control's own click is swallowed). And a press on a
      // NESTED row's grip belongs to that row's own container: every ancestor
      // container also contains the grip, and without this the outer list would
      // pick up the subtask's parent instead.
      filter: (evt) => {
        const el = evt.target;
        if (!el || !el.closest) return false;
        if (el.closest('button, a, input, textarea, select, label, [contenteditable="true"]')) return true;
        const item = el.closest('g-task-item');
        return !!item && item.parentElement !== container;
      },
      preventOnFilter: false,
      animation: 150,
      handle: '.g-task-handle',
      draggable: 'g-task-item',
      ...DRAG_CLASSES,
      onStart: () => { this._isDragging = true; },
      onEnd: (evt) => {
        this._isDragging = false;
        this._handleDragEnd(evt);
        if (this._pendingRender) { this._pendingRender = false; this.scheduleRender(); }
      },
      onUnchoose: () => { this._isDragging = false; },
    });
  }

  _destroySortables() {
    for (const s of this._sortables.values()) { try { s.destroy(); } catch { /* already gone */ } }
    this._sortables.clear();
  }

  /** The surface is about to be replaced by a skeleton/empty/compact body. */
  _teardownSurface() {
    this._destroySortables();
    this._surfaceEl = null;
    this._listEl = null;
    this._headEl = null;
    this._footEl = null;
    this._itemById = new Map();
  }

  /** The tasks directly in `container`, in on-screen order. */
  _idsIn(container) {
    return [...container.children]
      .filter((el) => el.tagName === 'G-TASK-ITEM')
      .map((el) => el.getAttribute('task-id'));
  }

  _handleDragEnd(evt) {
    if (!this._listEl) return;
    const container = evt.from;
    const taskId = evt.item.getAttribute('task-id');
    const siblingIds = this._idsIn(container);
    const tree = this._tree();

    // Children of every container on screen, in their new order; anything not
    // on screen keeps the order it had.
    const childOrder = new Map();
    for (const item of this._itemById.values()) {
      if (item.isConnected) childOrder.set(item.getAttribute('task-id'), this._idsIn(item.childList));
    }

    // The top level: the page on screen spliced back at its offset. The DOM
    // holds ONE page, and the host replaces its whole collection from `tasks`,
    // so emitting a single page would truncate the list and persist that.
    const rows = this._rows();
    const onPage = this._idsIn(this._listEl).map((id) => this._taskById.get(id)).filter(Boolean);
    const start = this._pageStart();
    const roots = this.paginated
      ? [...rows.slice(0, start), ...onPage, ...rows.slice(start + onPage.length)]
      : onPage;

    // Depth-first: every task right after its parent's earlier siblings'
    // subtrees, so sibling order survives at every level of the flat array.
    const ordered = [];
    const seen = new Set();
    const walk = (t) => {
      const id = this._taskId(t);
      if (seen.has(id)) return;
      seen.add(id);
      ordered.push(t);
      const kids = childOrder.has(id)
        ? childOrder.get(id).map((k) => this._taskById.get(k)).filter(Boolean)
        : (tree.kids.get(id) || []);
      kids.forEach(walk);
    };
    roots.forEach(walk);
    for (const t of this._tasks) if (!seen.has(this._taskId(t))) ordered.push(t);

    // Adopt the on-screen order immediately: the host persists and re-pushes
    // `.tasks` asynchronously, and any internal re-render in that window would
    // otherwise rebuild from the stale array and undo the drop.
    if (ordered.length === this._tasks.length) this._tasks = ordered;

    const parentId = container === this._listEl ? null : (container.parentElement && container.parentElement.getAttribute('task-id'));
    this.dispatchEvent(new CustomEvent('task-moved', {
      detail: {
        taskId,
        parentId: parentId || null,
        oldIndex: evt.oldIndex,
        newIndex: evt.newIndex,
        tasks: ordered,
        // `tasks` is only what this list holds — a filtered list is a subset.
        // The neighbours among its SIBLINGS let a host place the task in the
        // whole collection's order.
        ...neighbours(siblingIds, taskId),
      },
      bubbles: true, composed: true,
    }));
  }

  // ── Delegated interaction ──

  _emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  /**
   * `task-toggled` for one task, at any depth. `done` alone cannot express a
   * stage change: a stage-backed host has to write `status: "Done"`, and only
   * this component knows which value that is, so `field`/`value` say what to
   * persist.
   */
  _toggleTask(taskId, task, checked) {
    const spec = this._doneSpec();
    const detail = { taskId, done: checked, task };
    if (spec) {
      detail.field = spec.field;
      detail.value = checked ? spec.doneValue : spec.undoValue;
    }
    this._emit('task-toggled', detail);
  }

  /** The row an event happened in — the nearest task, which is the row's own. */
  _itemOf(el) {
    const itemEl = el && el.closest && el.closest('g-task-item');
    return itemEl && this.contains(itemEl) ? itemEl : null;
  }

  _handleChange = (e) => {
    if (!e.target.getAttribute || e.target.getAttribute('data-role') !== 'task-toggle') return;
    const itemEl = this._itemOf(e.target);
    if (!itemEl) return;
    const taskId = itemEl.getAttribute('task-id');
    this._toggleTask(taskId, this._taskById && this._taskById.get(taskId), e.target.checked);
  };

  _handleClick = (e) => {
    // Page buttons first: they sit inside the list surface, so without this the
    // interactive-child guard below would swallow them.
    const pageBtn = e.target.closest('[data-page]');
    if (pageBtn && this.contains(pageBtn)) {
      e.stopPropagation();
      this.setPage(this._page + (pageBtn.getAttribute('data-page') === 'next' ? 1 : -1));
      return;
    }

    // The count badge opens and closes the subtasks. Expanding is not
    // selecting: the click is stopped here, at the boundary, because hosts
    // routinely hang a row/card handler on an ancestor.
    const expandBtn = e.target.closest('[data-role="task-expand"]');
    if (expandBtn && this.contains(expandBtn)) {
      e.stopPropagation();
      e.preventDefault();
      const itemEl = this._itemOf(expandBtn);
      if (!itemEl) return;
      const id = itemEl.getAttribute('task-id');
      const open = !this._expanded.has(id);
      if (open) this._expanded.add(id); else this._expanded.delete(id);
      itemEl.setExpanded(open);
      return;
    }

    // "Add subtask": the host opens its create form with this task as parent.
    // The parent is opened as well, so the new subtask shows where it landed.
    const addBtn = e.target.closest('[data-role="subtask-add"]');
    if (addBtn && this.contains(addBtn)) {
      e.stopPropagation();
      e.preventDefault();
      const itemEl = this._itemOf(addBtn);
      if (!itemEl || !this.addSubtasks) return;
      const parentId = itemEl.getAttribute('task-id');
      this._expanded.add(parentId);
      itemEl.setExpanded(true);
      this._emit('add-subtask', {
        parentId,
        parentField: this._parentKey(),
        parent: this._taskById && this._taskById.get(parentId),
      });
      return;
    }

    // Ignore clicks on interactive children and the drag handle, and anything
    // outside a row (a children container's indent belongs to no task).
    if (e.target.closest('button, a, input, select, textarea, label, .g-task-handle')) return;
    if (!e.target.closest('.g-task-row')) return;
    const itemEl = this._itemOf(e.target);
    if (!itemEl) return;
    const taskId = itemEl.getAttribute('task-id');
    this._emit('task-clicked', { taskId, task: this._taskById && this._taskById.get(taskId) });
  };
}

function repeat(n, fn) { let o = ''; for (let i = 0; i < n; i++) o += fn(); return o; }

// Mirrors the real row's cell widths so the loading state doesn't reflow into
// a different shape once data arrives.
function skeletonRow(hasPills) {
  return `<div class="flex items-center gap-2 px-2 py-1.5 border-b border-base-300 last:border-b-0">
    <span class="w-4 shrink-0"></span>
    <div class="skeleton h-4 w-4 rounded shrink-0"></div>
    <div class="skeleton h-4 flex-1"></div>
    ${hasPills ? '<div class="skeleton h-4 w-24 shrink-0 hidden sm:block"></div>' : ''}
    <div class="skeleton h-4 w-20 shrink-0"></div>
  </div>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-task-list')) {
  customElements.define('g-task-list', GTaskList);
}
